/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";

import { apiFetch } from "../apiBindings";
import { PageContext } from "../components/shared/Page";
import {
    storageLocal as storage,
    storageSession as session,
} from "../utils/StorageHandler";
import { deliveryTimeOptions } from "./DeliveryTime";

export const DataContext = createContext();

const PelletsDataProvider = ({ children }) => {
    const pageContext = useContext(PageContext);
    const { updateLastCalculator } = pageContext.actions;

    const [zipcode, setZipcode] = useState(storage.get("pel:zipcode", ""));
    const [amount, setAmount] = useState(storage.getInt("pel:amount", 4000));
    const [product, setProduct] = useState(
        storage.get("pel:product-type", "loose")
    );
    /**
     * Result with prices
     */
    const [result, setResult] = useState([]);
    const [additions, setAdditions] = useState(null);

    const [loading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState(false);

    const [isCalculatorReady, setIsCalculatorReady] = useState(false);
    const [zipcodeError, setZipcodeError] = useState(false);
    const [zipcodeIsValid, setZipcodeIsValid] = useState(true);
    const [amountError, setAmountError] = useState(false);
    const [checkingForZipcode, setCheckingForZipcode] = useState(false);

    const firstZipcodeUpdate = useRef(true);

    /**
     * Chosen product
     */
    const [productChoice, setProductChoice] = useState(
        storage.getInt("pel:product-choice", 0)
    );
    /**
     * Sync zipcode with local storage.
     */
    useEffect(() => {
        storage.set("pel:zipcode", zipcode);
        if (firstZipcodeUpdate.current) {
            firstZipcodeUpdate.current = false;
            return;
        }
        if (!loading) {
            calculateIfPossible();
        }
    }, [zipcode]);

    /**
     * Sync amount with local storage.
     */
    useEffect(() => {
        storage.set("pel:amount", amount);
    }, [amount]);

    useEffect(() => {
        if (product !== null && result !== null) {
            storage.set("pel:product-type", product);
            setResult(null);
        }
        if (!loading) {
            calculateIfPossible();
        }
    }, [product]);

    /**
     * Sync product choice with local storage.
     */
    useEffect(() => {
        storage.set("pel:product-choice", productChoice);
    }, [productChoice]);

    /**
     * Sync product type with local storage.
     */
    useEffect(() => {
        storage.set("pel:product-type", product);
    }, [product]);

    /**
     * Sync the product infos into the local storage.
     */
    const syncProductInfos = (product) => {
        if (product) {
            storage.set("pel:product-id", product.id);
            if (product.has_wish_date) {
                storage.set("pel:delivery-time-type", deliveryTimeOptions.date);
            } else {
                storage.set(
                    "pel:delivery-time-type",
                    deliveryTimeOptions.standard
                );
            }
        } else {
            storage.set("pel:delivery-time-type", deliveryTimeOptions.standard);
        }
    };

    const syncCalculatorInput = (productId) => {
        storage.set("pel:order-product-id", productId);
        storage.set("pel:order-product-type", product);
        storage.set("pel:order-zipcode", storage.get("pel:zipcode"));
        storage.set("pel:order-amount", storage.get("pel:amount"));

        const address = storage.getJson("pel:delivery-address");
        if (storage.get("pel:zipcode") !== address?.zipcode) {
            storage.setJson("pel:delivery-address", {
                ...address,
                city: "",
                street: "",
                zipcode: "",
            });
        }
    };

    /**
     * Checks if the zipcode is valid
     * @param {*} localZipcode
     */
    const checkZipcodeLoose = async (localZipcode) => {
        setCheckingForZipcode(true);

        const repsonse = await apiFetch(
            `/pellets/check-zipcode/loose/${localZipcode}`
        );
        const data = await repsonse.json();

        setZipcodeIsValid(data.is_valid);
        setCheckingForZipcode(false);
    };

    /**
     * Checks if the zipcode is valid
     * @param {*} localZipcode
     */
    const checkZipcodePallet = async (localZipcode) => {
        setCheckingForZipcode(true);

        const repsonse = await apiFetch(
            `/pellets/check-zipcode/pallet/${localZipcode}`
        );
        const data = await repsonse.json();

        setZipcodeIsValid(data.is_valid);
        setCheckingForZipcode(false);
    };

    /**
     * Calculate if the configuration is valid.
     */
    const calculateIfPossible = async () => {
        setIsCalculatorReady(false);
        // When we are in the order step, we should take the order parameters.. :feelsGoodEnoughMan
        const isOrderUrl = window.location.href.includes("/bestellung/");

        const localZipcode = isOrderUrl
            ? storage.get("pel:order-zipcode")
            : zipcode;
        const localAmount = isOrderUrl
            ? storage.get("pel:order-amount")
            : amount;
        const localProduct = isOrderUrl
            ? storage.get("pel:order-product-type")
            : product;

        if (localZipcode && localZipcode !== "" && localAmount) {
            if (localProduct === "loose") {
                await calculateLoose(localZipcode, localAmount);
            }
            if (localProduct === "bags") {
                await calculatePalletBags(localZipcode, localAmount);
            }
            if (localProduct === "big-bag") {
                await calculatePalletBigBags(localZipcode, localAmount);
            }
            setIsCalculatorReady(true);
        } else {
            setIsCalculatorReady(true);
        }
    };

    /**
     * Clear the checkout before we go in the order again.
     */
    const clearCheckout = () => {
        storage.remove("pel:delivery-time-type");
        storage.remove("pel:delivery-time-frame");
        storage.remove("pel:delivery-time-wish-date");
        storage.remove("pel:hose");
        storage.remove("pel:needs-session");

        session.remove("pel:order");
        session.remove("pel:checkout");
        storage.remove("pel:selected-city");
        storage.remove("pel:selected-street");
        // @TODO Don't forget to clear the additional addresses, when the are implemented
        // storage.remove("pel:additional-addresses", []);
    };

    /**
     * Fetch the prices for loose pellets from the api and store them into the result.
     */
    const calculateLoose = async (localZipcode, localAmount) => {
        if (zipcodeError || !zipcodeIsValid || amountError) {
            return;
        }

        updateLastCalculator("holzpellets");

        if (!loading) {
            // do the calculation
            setLoading(true);
            return apiFetch(`/pellets/loose/${localZipcode}/${localAmount}`)
                .then((response) => response.json())
                .then((data) => {
                    if (data.status && data.status === "error") {
                        setResult([]);
                        setAdditions(null);
                        setLoadingError(true);
                    } else {
                        setResult(data.products);
                        setAdditions(data.settings);
                        setLoadingError(false);
                    }
                    setLoading(false);
                });
        } else {
            // Still loading, do nothing
        }
    };

    /**
     * Fetch the prices for bags from the api and store them into the result.
     */
    const calculatePalletBags = async (localZipcode, localAmount) => {
        if (zipcodeError || !zipcodeIsValid || amountError) {
            return;
        }

        storage.set("last-calculator", "holzpellets");

        if (!loading) {
            // do the calculation
            setLoading(true);
            return apiFetch(
                `/pellets/pallet/bags/${localZipcode}/${localAmount}`
            )
                .then((response) => response.json())
                .then((data) => {
                    if (data.status && data.status === "error") {
                        setResult([]);
                        setAdditions(null);
                        setLoadingError(true);
                    } else {
                        setResult(data.products);
                        setAdditions(data.settings);
                        setLoadingError(false);
                    }
                    setLoading(false);
                });
        } else {
            // Still loading, do nothing
        }
    };

    /**
     * Fetch the prices for big bags from the api and store them into the result.
     */
    const calculatePalletBigBags = async (localZipcode, localAmount) => {
        if (zipcodeError || !zipcodeIsValid || amountError) {
            return;
        }

        storage.set("last-calculator", "holzpellets");

        if (!loading) {
            // do the calculation
            setLoading(true);
            return apiFetch(
                `/pellets/pallet/big-bag/${localZipcode}/${localAmount}`
            )
                .then((response) => response.json())
                .then((data) => {
                    if (data.status && data.status === "error") {
                        setResult([]);
                        setAdditions(null);
                        setLoadingError(true);
                    } else {
                        setResult(data.products);
                        setAdditions(data.settings);
                    }
                    setLoading(false);
                });
        } else {
            // Still loading, do nothing
        }
    };

    const data = {
        result,
        zipcode,
        amount,
        product,
        productChoice,
        zipcodeIsValid,
        checkingForZipcode,
        zipcodeError,
        additions,
        amountError,
        loadingError,
        isCalculatorReady,
    };

    const actions = {
        setZipcode,
        setAmount,
        calculateLoose,
        calculatePalletBags,
        calculatePalletBigBags,
        setProduct,
        setProductChoice,
        setZipcodeIsValid,
        setCheckingForZipcode,
        checkZipcodeLoose,
        checkZipcodePallet,
        setZipcodeError,
        setResult,
        setAdditions,
        syncProductInfos,
        syncCalculatorInput,
        clearCheckout,
        setAmountError,
    };

    return (
        <DataContext.Provider
            value={{
                data,
                actions,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default PelletsDataProvider;
